import { useEffect, useRef } from 'react';
import lottie from 'lottie-web';

/**
 * Plays a lottie animation inside `containerRef` for the lifetime of the component.
 *
 * lottie keeps every loaded animation in a module-level registry and renders it on every animation
 * frame until `destroy()` is called, so a component that forgets to destroy pins its container and the
 * whole unmounted React subtree above it. This hook owns that lifecycle for the workspace apps, where
 * direct `lottie-web` imports are blocked by `no-restricted-imports`.
 *
 * `animationData` and `path` are effect dependencies: pass a module constant or a memoized value,
 * since a fresh object each render destroys and reloads the animation every render.
 *
 * @param {React.RefObject<HTMLElement>} containerRef - Element the animation renders into
 * @param {object} options
 * @param {object} [options.animationData] - Inline animation JSON (use this or `path`)
 * @param {string} [options.path] - URL of the animation JSON (use this or `animationData`)
 * @param {boolean} [options.loop=true]
 * @param {boolean} [options.autoplay=true]
 * @param {(animation: object) => void} [options.onLoaded] - Called with the AnimationItem once its DOM is built
 */
export const useLottieAnimation = (containerRef, { animationData, path, loop = true, autoplay = true, onLoaded } = {}) => {
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    const animation = lottie.loadAnimation({ container: containerRef.current, renderer: 'svg', loop, autoplay, animationData, path });
    animation.addEventListener('DOMLoaded', () => onLoadedRef.current?.(animation));
    return () => animation.destroy();
  }, [animationData, path, loop, autoplay]);
};
